import { useState, useEffect, useCallback, useMemo } from "react";
import TourPricingService from "../services/tourPricingService";

/**
 * Hook personalizado para manejar los cálculos de precios del tour
 * @param {Object} tourData - Datos del tour
 * @param {Object} options - Opciones seleccionadas
 * @param {Array} tourPrices - Lista de precios del tour
 * @param {Array} gpsPrices - Lista de precios de GPS
 * @param {Array} insurancePrices - Lista de precios de seguros
 * @param {Object} discounts - Descuentos aplicados
 * @returns {Object} Estado y funciones relacionadas con los precios
 */
export const useTourPricing = (tourData, options, tourPrices, gpsPrices, insurancePrices, discounts = {}) => {
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Calcula los precios cuando cambian las dependencias
   */
  const calculatePricing = useCallback(async () => {
    if (!tourData || !options || !tourPrices || !gpsPrices || !insurancePrices) {
      setPricing({});
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const pricingResult = TourPricingService.calculateTotalPricing(
        tourData,
        options,
        tourPrices,
        gpsPrices,
        insurancePrices,
        discounts
      );

      // Calcular valor a cargar
      const valueToCharge = TourPricingService.calculateValueToCharge(
        tourData,
        pricingResult,
        options && options.cancelInsurance
      );

      setPricing({
        ...pricingResult,
        valueToCharge
      });

    } catch (err) {
      console.error("Error calculating pricing:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tourData, options, tourPrices, gpsPrices, insurancePrices, discounts]);

  /**
   * Recalcula los precios
   */
  const recalculate = useCallback(() => {
    calculatePricing();
  }, [calculatePricing]);

  /**
   * Obtiene el desglose de precios en formato legible
   */
  const pricingBreakdown = useMemo(() => {
    if (!pricing.breakdown) return [];

    const breakdown = [];

    // Precio base
    if (pricing.breakdown.basePrice > 0) {
      breakdown.push({
        label: "Precio Base",
        value: pricing.breakdown.basePrice,
        type: "base"
      });
    }

    // GPS
    if (pricing.breakdown.gpsPrice > 0) {
      breakdown.push({
        label: `GPS (${pricing.breakdown.effectiveDays} días)`,
        value: pricing.breakdown.gpsPrice,
        type: "extra"
      });
    }

    // Seguro VIP
    if (pricing.breakdown.vipInsurancePrice > 0) {
      breakdown.push({
        label: `Seguro VIP (${pricing.breakdown.effectiveDays} días)`,
        value: pricing.breakdown.vipInsurancePrice,
        type: "extra"
      });
    }

    // Seguro de cancelación
    if (pricing.breakdown.cancelInsurancePrice > 0) {
      breakdown.push({
        label: "Seguro de Cancelación",
        value: pricing.breakdown.cancelInsurancePrice,
        type: "insurance"
      });
    }

    // Pasajero
    if (pricing.breakdown.passengerPrice > 0) {
      breakdown.push({
        label: "Pasajero",
        value: pricing.breakdown.passengerPrice,
        type: "passenger"
      });
    }

    // Habitación individual
    if (pricing.breakdown.singlePrice > 0) {
      breakdown.push({
        label: "Habitación Individual",
        value: pricing.breakdown.singlePrice,
        type: "room"
      });
    }

    // Modelo de vehículo
    if (pricing.breakdown.modelPrice > 0) {
      breakdown.push({
        label: "Modelo de Vehículo",
        value: pricing.breakdown.modelPrice,
        type: "vehicle"
      });
    }

    // Descuentos
    if (discounts.rewards > 0) {
      breakdown.push({
        label: "Recompensas",
        value: -discounts.rewards,
        type: "discount"
      });
    }

    if (discounts.discount > 0) {
      breakdown.push({
        label: "Descuento",
        value: -discounts.discount,
        type: "discount"
      });
    }

    return breakdown;
  }, [pricing.breakdown, discounts]);

  /**
   * Obtiene el total de extras
   */
  const totalExtras = useMemo(() => {
    if (!pricing.breakdown) return 0;

    return (
      pricing.breakdown.gpsPrice +
      pricing.breakdown.vipInsurancePrice +
      pricing.breakdown.passengerPrice +
      pricing.breakdown.singlePrice +
      pricing.breakdown.modelPrice
    );
  }, [pricing.breakdown]);

  /**
   * Obtiene el total de descuentos
   */
  const totalDiscounts = useMemo(() => {
    return (discounts.rewards || 0) + (discounts.discount || 0);
  }, [discounts]);

  /**
   * Obtiene el ahorro total
   */
  const totalSavings = useMemo(() => {
    return totalDiscounts;
  }, [totalDiscounts]);

  /**
   * Verifica si hay cambios significativos en el precio
   */
  const hasPriceChanges = useMemo(() => {
    if (!pricing.finalTotal || !tourData) return false;

    // Comparar con el precio original del tour
    const originalPrice = tourData.rental_price || 0;
    const priceDifference = Math.abs(pricing.finalTotal - originalPrice);

    // Considerar cambio significativo si la diferencia es mayor al 5%
    return priceDifference > (originalPrice * 0.05);
  }, [pricing.finalTotal, tourData]);

  /**
   * Obtiene el porcentaje de descuento
   */
  const discountPercentage = useMemo(() => {
    if (!pricing.finalTotal || totalDiscounts === 0) return 0;

    return (totalDiscounts / pricing.finalTotal) * 100;
  }, [pricing.finalTotal, totalDiscounts]);

  /**
   * Obtiene información sobre el valor a cargar
   */
  const chargeInfo = useMemo(() => {
    if (!pricing.valueToCharge || !pricing.finalTotal) return null;

    const isFullCharge = pricing.valueToCharge >= pricing.finalTotal;
    const isPartialCharge = pricing.valueToCharge < pricing.finalTotal && pricing.valueToCharge > 0;
    const remainingAmount = pricing.finalTotal - pricing.valueToCharge;

    return {
      isFullCharge,
      isPartialCharge,
      remainingAmount,
      chargePercentage: (pricing.valueToCharge / pricing.finalTotal) * 100
    };
  }, [pricing.valueToCharge, pricing.finalTotal]);

  /**
   * Obtiene advertencias sobre los precios
   */
  const pricingWarnings = useMemo(() => {
    const warnings = [];

    // Advertencia si el valor a cargar es muy bajo
    if (pricing.valueToCharge > 0 && pricing.valueToCharge < pricing.finalTotal * 0.1) {
      warnings.push("El valor a cargar es muy bajo comparado con el total");
    }

    // Advertencia si no se seleccionó seguro de cancelación para tours largos
    if (options && !options.cancelInsurance && pricing.breakdown && pricing.breakdown.effectiveDays > 7) {
      warnings.push("Considere agregar seguro de cancelación para tours largos");
    }

    // Advertencia si el descuento es muy alto
    if (discountPercentage > 20) {
      warnings.push("El descuento aplicado es muy alto");
    }

    return warnings;
  }, [pricing.valueToCharge, pricing.finalTotal, options && options.cancelInsurance, pricing.breakdown && pricing.breakdown.effectiveDays, discountPercentage]);

  // Calcular precios cuando cambian las dependencias
  useEffect(() => {
    calculatePricing();
  }, [calculatePricing]);

  return {
    // Estado
    pricing,
    loading,
    error,

    // Cálculos derivados
    pricingBreakdown,
    totalExtras,
    totalDiscounts,
    totalSavings,
    hasPriceChanges,
    discountPercentage,
    chargeInfo,
    pricingWarnings,

    // Funciones
    recalculate
  };
}; 


// WEBPACK FOOTER //
// ./src/hooks/useTourPricing.js